import { CSSProperties, Dispatch } from 'react';
import { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import { IConfigProps } from '../context/ConfigContext';
import { IUserContextProps } from '../context/UserContext';
import { Endpoint } from '../store/Actions';

export type Generic<T = unknown> = Record<string, T>;

export enum ProductNames {
    CAR = 'CAR',
    CHILD = 'CHILD'
}

export enum WhitelabelPartner {
    NONE = '',
    DANSK_BILBRANCHERÅD = 'DANSK_BILBRANCHERÅD',
    NORDEA = 'NORDEA',
    BABY_SAM = 'BABY_SAM'
}

export type ProductKey = ProductNames.CAR | ProductNames.CHILD;

export enum PackageInfo {
    COMPREHENSIVE = 'COMPREHENSIVE',
    LIABILITY_ONLY = 'LIABILITY_ONLY'
}

export enum BLOCK_TYPES {
    CAR_NOT_IN_LIST = 'CAR_NOT_IN_LIST',
    LEASING_AGE_INCOMPATIBLE = 'LEASING_AGE_INCOMPATIBLE',
    CAR_TOO_OLD = 'CAR_TOO_OLD',
    D_STATUS_EARLY = 'D-STATUS-EARLY',
    D_STATUS_LATE = 'D-STATUS-LATE',
    CAR_DATA_DEFICIENT = 'CAR_DATA_DEFICIENT',
    CUSTOMER_BELOW_18 = 'CUSTOMER_BELOW_18',
    TOO_MANY_CLAIMS = 'TOO_MANY_CLAIMS',
    UNSUPPORTED_ADDRESS_BORNHOLM = 'UNSUPPORTED_ADDRESS_BORNHOLM',
    UNSUPPORTED_ADDRESS_BBR = 'UNSUPPORTED_ADDRESS_BBR',
    CAR_ALREADY_COVERED = 'CAR_ALREADY_COVERED',
    CUSTOMER_MARKING = 'CUSTOMER_MARKING', // (customer marking)
    GENERAL_ERROR = 'GENERAL_ERROR',
    WILL_NOT_INFORM_CPR = 'WILL_NOT_INFORM_CPR',
    SALES_COMPLETE = 'SALES_COMPLETE',
    EXCLUDED_MANUFACTURER = 'EXCLUDED_MANUFACTURER',
    CAR_OTHER_NEEDS = 'CAR_OTHER_NEEDS',
    CAR_NO_CONTENT_37_38 = 'CAR_NO_CONTENT_37_38'
}

export interface ProductPage {
    boxTitle?: string;
    title?: string;
    titleStyle?: CSSProperties;
    component: React.JSXElementConstructor<any>;
    continueFn?: ({ config, dispatch, selection, model, user, pContext }: ContinueFn) => void;
    onLoadFn?: ({ config, dispatch, selection, model, user }: OnloadFn) => void;
    flow: 'ALL' | 'CUSTOMER' | 'NONCUSTOMER';
    hasNavigation?: boolean;
    hasGiftcard?: boolean;
    progressBarStep?: number;
    key: string;
    order: number;
    path: string;
    requiresLogin: boolean;
    size?: string;
    validateFields?: string[];
    children?: unknown; //React.ReactElement
}

export interface IAction {
    type: string;
    payload: object | string | number | boolean | Generic<any>;
}

export interface IRouterPage extends ProductPage {
    initFieldsOnLoad?: string[];
    hideProgressbar?: boolean;
    isLoaderPage?: boolean;
    primaryBtnText?: string;
    altTitle?: string;
    altPrimaryBtnText?: string;
    altConditions?: string;
}

export interface TProvider {
    children: React.ReactNode | React.ReactElement;
}

export interface TComponent {
    pageKey: string;
    flow: IRouterPage[];
    onLoadFn: ({ config, dispatch, selection, model, user }: OnloadFn) => void;
    boxTitle?: string;
    validateFields: string[];
    currentPage?: IRouterPage;
}

export interface TExperienceComponent extends Omit<TComponent, 'onLoadFn'> {
    onLoadFn: ({
        config,
        dispatch,
        selection,
        model,
        user,
        logGetCarVerificationError
    }: OnloadFnExperiencePage) => void;
}

export interface TPackageLoaderComponent extends Omit<TComponent, 'onLoadFn'> {
    onLoadFn: ({
        config,
        dispatch,
        selection,
        model,
        user,
        logHandlePackagesRequestError
    }: OnloadFnPackagePage) => void;
}

export interface TSimpleComponent {
    boxTitle?: string;
}

export interface TWizard {
    handleFinalize: () => void;
    pageKey: string;
    flow: IRouterPage[];
}

export enum PackageNames {
    BASIS = 'Basis',
    STANDARD = 'Standard',
    PREMIUM = 'Premium'
}

export interface Error {
    redirectToPage: string | undefined;
    errorMessage: string;
    hasError: boolean;
    manufacturerDataDeficient: boolean;
}

interface LeasingCompany {
    companyCode: string;
    companyId: string;
    name: string;
}

type PartnerInfo = {
    vin: string;
    model: string;
    cvr: string;
    dealer: string;
    agent: string;
    key: string;
};

interface RegistrationInfo {
    leasing: boolean;
    leasingFrom: string;
    leasingTo: string;
    company: LeasingCompany;
}

export interface DMR {
    manufacturerName: string;
    modelName: string;
    variantTypeName: string;
    vehicleType: string;
    originalRegistrationDate: string;
    olderCar: boolean;
    insuranceRequired: boolean;
    tariffGroup: number;
    trackingUnit: boolean;
    trackingUnitShown: boolean;
    isLeasingCar?: boolean;
    registration?: RegistrationInfo;
    topVariantId: string;
    vehicleTypeError: { typeId: string; typeName: string } | null;
}

export interface Coverage {
    coverageAlias: string;
    coverageId: number;
    deductableAmount: number;
    deductableBasis: number;
    name: string;
    selected: boolean;
    showInPackage: boolean;
    enabled?: boolean;
    price: number;
}

export interface Package {
    coverages: Coverage[];
    defaultSelected: boolean;
    monthlyPrice: number;
    objectId: number;
    packageName: PackageNames.BASIS | PackageNames.STANDARD | PackageNames.PREMIUM;
    productDiscountPct: number;
    productId: number;
    productName: string;
    productTitle: string;
    quoteId: number;
    yearlyPrice: number;
}

export interface Stakeholder {
    country: string;
    cprNo: string;
    customerId: string;
    emailAddress: string;
    emailPriority: number;
    firstName: string;
    lastName: string;
    mobilePhone: string;
    streetAddress: string;
    village: string;
    zipCity: string;
}

export interface GiftCard {
    amount: string;
    key: string;
    status: 'active' | 'inactive';
    campaign: string;
}

export interface EBoks {
    brand: string;
    idNum: string;
    stakeholderId: string;
    status: string;
    statusTxt: string;
    subscribed: boolean;
    type: string;
}

export interface PaymentService {
    hasMPSAgreement: boolean;
    hasPBSAgreement: boolean;
}

interface Status {
    canProceed: boolean;
    message: string;
    code: string;
}

interface CarStatus extends Status {
    haveContentInsurance: boolean | null;
}

/**
 * Labels is initalized per product
 * const labels: Labels;
 * labels[config.product].label_key => labels[ProductNames.CAR].numberPlate
 */
export interface Labels {
    [product: string]: Generic<string | null>;
}

export interface Selection {
    partner: WhitelabelPartner;
    isCustomer?: boolean;
    name?: string;
    cprNo?: string;
    fullName?: string;
    phoneNumber?: string;
    address?: FieldEventDataProps;
    vehicleSelectionMode?: string;
    registrationNumber?: string;
    viNumber: string;
    manufactor?: string;
    fuel?: string;
    model?: string;
    vintage?: string;
    motorSize?: string;
    power?: string;
    variantId?: string;
    carNotInList?: boolean;
    mileage?: number | null;
    ownCarYears?: number;
    numberOfClaims?: number;
    latestClaimYear?: number;
    numberOfChildren?: number;
    disabilitySum?: number;
    didClickFinalize: boolean;
    didClickFetchPackages: boolean;
    package: Package | null;
    deductible?: number;
    selectedAdditionalCoverages?: SelectedAdditionalCoverage[];
    currentInsuranceCompany?: string;
    preSelectedInsuranceCompany?: string;
    effectiveDate?: string;
    alreadyCovered?: string;
    premiumRate?: string;
    paymentOption?: string;
    pbs?: string;
    bankRegNumber: string;
    bankAccountNumber: string;
    packageInfo?: PackageInfo | null;
    isNeedOtherCoverage?: boolean;
    partnerInfo?: PartnerInfo | null;
    [key: string]: unknown;
}

export interface SelectedAdditionalCoverage {
    coverageAlias: string;
    selected: boolean;
}

export interface Deductible {
    amount: number;
    defaultDeductible: boolean;
    deductibleBasis: number;
}

interface CustomerMarking {
    canProceed: boolean;
    message: string;
}

export type KeyValue = {
    label: string;
    value: string;
};

export interface Packages {
    additionalCoverages: string[];
    brand: string;
    customerId: string;
    deductibles: Deductible[];
    hasProductDiscount: boolean;
    packages: Package[];
    productKey: ProductKey;
}

export interface Car extends Generic<unknown> {
    dmr: DMR | null;
    packages: Packages | null;
    stakeholder: Stakeholder | null;
    status: CarStatus | null;
    giftcard: GiftCard | null;
    eboks: EBoks | null;
    pbs: PaymentService | null;
    customerMarking: CustomerMarking | null;
    coverageTexts: unknown;
    manufactors: KeyValue[];
    fuels: KeyValue[];
    models: KeyValue[];
    years: KeyValue[];
    engines: KeyValue[];
    powers: KeyValue[];
    dfimStatus: boolean;
}

export interface Child extends Generic<unknown> {
    packages: Packages | null;
    stakeholder: Stakeholder | null;
    numberOfChildren: number;
    childList: IChild[] | [];
    pbs: PaymentService | null;
    customerMarking: CustomerMarking | null;
    coverageTexts: unknown;
    disabilitySum: number;
    skipCriticalIllnessPage: boolean | null;
}

export interface Model extends Generic<Car | Child> {
    CAR: Car;
    CHILD: Child;
}

export interface States {
    activePage: IRouterPage;
    error: Error;
    purchaseComplete: boolean;
    reloadStakeholder: boolean;
    selection: Selection;
    fieldValidation: {
        [key: string]: boolean;
    };
    model: Model;
    labels: Labels;
    shouldCheckBBR: boolean;
    runCriticalIllnessSplitTest: boolean;
}

interface IFieldValidation {
    isValid: boolean | undefined;
    isExist: boolean | undefined;
}

export interface IChild {
    fullName: string;
    cprNumber: string;
    cprValidation?: IFieldValidation;
    nameValidation?: IFieldValidation;
    cancelPreviousInsurance?: boolean;
    age: number | undefined;
}

// Anyone with a better name for this type?
export type OnloadFn = {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selection?: Selection;
    user?: Stakeholder | IUserContextProps;
    model?: Model;
};

export type OnloadFnExperiencePage = OnloadFn & {
    logGetCarVerificationError?: (errorMessage: string) => void;
};

export type OnloadFnPackagePage = OnloadFn & {
    logHandlePackagesRequestError?: (errorMessage: string) => void;
};

export type OnloadFnFinalizePurchasePage = OnloadFn & {
    logFinalizePurchaseError: (errorMessage: string) => void;
    logFinalizePurchaseEmailError: (errorMessage: string) => void;
};

export type ContinueFn = {
    dispatch: Dispatch<IAction>;
    model: Model;
    user: IUserContextProps;
    config: IConfigProps;
    selection: Selection;
    pContext: PContext;
};

export type TeCommerce = Pick<ContinueFn, 'config' | 'selection' | 'pContext'>;

export type PContext = {
    selectedPackageIndex: number;
    selectedPackage: Package;
    selectedDeductible: number;
    packages: Package[];
    additionalCoverages: string[];
    selectedAdditionalCoverages: SelectedAdditionalCoverage[];
};

export type TError = {
    dispatch: Dispatch<IAction>;
    errorMessage: string;
    hasError: boolean;
    redirectToPage?: string;
    manufacturerDataDeficient?: boolean;
};

export type PackageResponse = {
    brand: string;
    productKey: ProductNames;
    customerId: string;
    hasProductDiscount: false;
    packages: Package[];
    additionalCoverages: string[];
};

export enum Flow {
    NOT_SET = 'Not set',
    CURRENT = 'Current'
}

/*
 * This is where the split tests are configured
 * By adding new entries to flowConfig a new split test is added
 * export const flowConfig = [{ index: 0, flow: Flow.CURRENT }, { index: 1, flow: Flow.NO_COVERAGES }]
 * The flow must also by added to list of Flow
 * index must be incremented with 1 for each flow: 0, 1, 2 etc
 */
export const flowConfig = [{ index: 0, flow: Flow.CURRENT }];

export type VinCheck = {
    vinToCheck: string;
    endpoint: Endpoint;
};

export type VehicleQualifier = 'REG' | 'VIN';
